import React, { useState } from 'react';
import { AssetUrls } from 'config/assets';
import { TopDeckedMark } from 'components/TopDeckedMark/TopDeckedMark';
import { useIsMobile } from 'hooks/useIsMobile';
import {
  TD_DECKS_DISPLAYED,
  TD_FORMATS_DISPLAYED,
  TD_GAMEPLAY_FORMATS_DISPLAYED,
  TD_MOMENTUM_DIRECTION,
  TD_POPULARITY_LABELS,
  TD_RATE_LABELS,
  formatTdPercentage,
} from 'constants/topdecked';
import {
  CardAnalyticsDeck,
  CardAnalyticsFormat,
  CardAnalyticsGameplay,
  useGetCardAnalyticsQuery,
} from 'store/services/cards/analytics/cardAnalyticsApi';
import 'components/ArtCropFade/ArtCropFade.css';
import './CardAnalyticsPanel.css';

interface Props {
  oracleId: string;
  /** The viewed printing's Scryfall id; deck rows with no artScryfallId fall back to it. */
  fallbackArtScryfallId: string;
}

/**
 * "How This Card Is Played" (ticket 1215930107055662). One module, up to three blocks,
 * rendered only for the data that exists. Reads GET /cards/:oracleId/analytics, which
 * the backend has already sorted, capped, junk-filtered and UTM-tagged; nothing here
 * orders, selects, or builds a topdecked.com URL. A card with no CardAnalytics row (404)
 * renders nothing at all: no heading, no empty state, no skeleton.
 *
 * Layout: an attached dark panel in the same stack as .card-wanters-panel. The panel
 * above always provides the seam (via :has() rules in ViewCardPage.css and
 * CardWantersTable.css), so this panel never carries a top border and always has square
 * top corners.
 */
export const CardAnalyticsPanel: React.FC<Props> = ({ oracleId, fallbackArtScryfallId }) => {
  const { data, isLoading } = useGetCardAnalyticsQuery({ oracleId }, { skip: !oracleId });
  const isMobile = useIsMobile();

  if (isLoading || !data) return null;

  const hasFormats = data.formats.length > 0;
  const hasDecks = data.topDecks.length > 0;
  const hasGameplay = data.gameplay.length > 0;
  if (!hasFormats && !hasDecks && !hasGameplay) return null;

  // Column template depends on which blocks exist; Format Play always gets the wide column.
  const blockCount = [hasFormats, hasDecks, hasGameplay].filter(Boolean).length;
  const layout =
    blockCount === 3 ? 'three' : blockCount === 2 ? (hasFormats ? 'two-wide' : 'two') : 'one';

  return (
    <section className="card-analytics-panel" aria-labelledby="cap-title">
      <header className="cap-header">
        <div className="cap-header-text">
          <h2 id="cap-title" className="cap-title">
            How This Card Is Played
          </h2>
          <p className="cap-subhead">
            Where this card shows up, and how players treat it once it is in hand.
          </p>
        </div>
        {/* rel=noopener only (ticket ruling): noreferrer would strip the Referer header,
            which is the one signal TopDecked gets besides the UTMs for reciprocal traffic. */}
        {/* eslint-disable-next-line react/jsx-no-target-blank */}
        <a
          className="cap-attribution"
          href={data.attributionUrl}
          target="_blank"
          rel="noopener"
        >
          <TopDeckedMark size={isMobile ? 14 : 16} className="cap-attribution-mark" />
          <span className="cap-attribution-text">
            Deck and gameplay data from <span className="cap-td">TopDecked</span>
          </span>
        </a>
      </header>

      <div className={`cap-grid cap-grid--${layout}`} data-testid="cap-grid">
        {hasFormats && <FormatPlayBlock formats={data.formats} />}
        {hasDecks && (
          <TopDecksBlock
            decks={data.topDecks}
            fallbackArtScryfallId={fallbackArtScryfallId}
            isMobile={isMobile}
          />
        )}
        {hasGameplay && <OpeningHandBlock gameplay={data.gameplay} />}
      </div>
    </section>
  );
};

const BlockHeading: React.FC<{ title: string; cap: number; children?: React.ReactNode }> = ({
  title,
  cap,
  children,
}) => (
  <div className="cap-block-heading">
    <span className="cap-block-name">{title}</span>
    <span className="cap-block-cap">TOP {cap}</span>
    {children}
  </div>
);

const FormatPlayBlock: React.FC<{ formats: CardAnalyticsFormat[] }> = ({ formats }) => (
  <div className="cap-block cap-block--formats">
    <BlockHeading title="Format Play" cap={TD_FORMATS_DISPLAYED} />
    <ul className="cap-format-list">
      {formats.map((f) => {
        const direction = TD_MOMENTUM_DIRECTION[f.momentum] ?? 'none';
        return (
          <li key={`${f.tier}-${f.format}`} className="cap-format-row">
            <div className="cap-format-main">
              <div className="cap-format-name-row">
                <span className="cap-format-name">{f.format}</span>
                <span className={`cap-tier cap-tier--${f.tier}`}>
                  {f.tier === 'competitive' ? 'COMPETITIVE' : 'CASUAL'}
                </span>
              </div>
              <div className="cap-format-line">
                Played in {formatTdPercentage(f.percentage)} of {f.format} decks
              </div>
            </div>
            <span className={`cap-popularity cap-popularity--${f.popularity}`}>
              {TD_POPULARITY_LABELS[f.popularity] ?? f.popularity}
            </span>
            {direction === 'none' ? (
              <span className="cap-momentum cap-momentum--none" aria-hidden="true" data-testid="cap-momentum-none" />
            ) : (
              <span
                className={`cap-momentum cap-momentum--${direction}`}
                data-testid={`cap-momentum-${direction}`}
                role="img"
                aria-label={direction === 'up' ? 'Trending up' : 'Trending down'}
              />
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

const TopDecksBlock: React.FC<{
  decks: CardAnalyticsDeck[];
  fallbackArtScryfallId: string;
  isMobile: boolean;
}> = ({ decks, fallbackArtScryfallId, isMobile }) => {
  // Rows whose art failed to load degrade to a flat row. Keyed by index; the src never
  // changes after an error, so there is no re-fetch loop.
  const [brokenArt, setBrokenArt] = useState<Record<number, boolean>>({});

  return (
    <div className="cap-block cap-block--decks" data-testid="cap-block-decks">
      <BlockHeading title="Top Decks" cap={TD_DECKS_DISPLAYED}>
        <span className="cap-on-topdecked">
          <TopDeckedMark size={isMobile ? 11 : 12} />
          on TopDecked
        </span>
      </BlockHeading>
      <ul className="cap-deck-list">
        {decks.map((deck, index) => {
          const artId = deck.artScryfallId || fallbackArtScryfallId;
          const showArt = !!artId && !brokenArt[index];
          return (
            <li key={`${deck.name}-${index}`} className="cap-deck-item">
              {/* rel=noopener only, same Referer reasoning as the attribution link. */}
              {/* eslint-disable-next-line react/jsx-no-target-blank */}
              <a
                className="cap-deck-row"
                href={deck.url}
                target="_blank"
                rel="noopener"
              >
                {showArt && (
                  <img
                    className="cap-deck-art art-crop-fade"
                    data-testid="cap-deck-art"
                    src={AssetUrls.cards.getCardImage(artId, 'art_crop')}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    onError={() => setBrokenArt((prev) => ({ ...prev, [index]: true }))}
                  />
                )}
                <span className="cap-deck-text">
                  <span className="cap-deck-name">{deck.name}</span>
                  <span className="cap-deck-domain">
                    topdecked.com <span aria-hidden="true">&#8599;</span>
                  </span>
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const OpeningHandBlock: React.FC<{ gameplay: CardAnalyticsGameplay[] }> = ({ gameplay }) => (
  <div className="cap-block cap-block--gameplay">
    <BlockHeading title="Opening Hand" cap={TD_GAMEPLAY_FORMATS_DISPLAYED} />
    <ul className="cap-gameplay-list">
      {gameplay.map((g) => (
        <li key={g.format} className="cap-gameplay-card">
          <span className="cap-gameplay-format">{g.format}</span>
          <span className="cap-gameplay-rate">
            {TD_RATE_LABELS[g.keepRate] ?? g.keepRate} keep rate
          </span>
          <span className="cap-gameplay-priority">
            {TD_RATE_LABELS[g.keepPriority] ?? g.keepPriority} keep priority
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export default CardAnalyticsPanel;
